import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { InkQuoteApi } from '../services/inkquoteApi';

const SkipCategoriesContext = createContext();

// Available skip categories
export const SKIP_CATEGORIES = {
  IMAGE_ONLY: 'image_only',
  LOW_QUALITY: 'low_quality',
  SPAM: 'spam',
  DUPLICATE: 'duplicate',
  LONG_TEXT: 'long_text',
  MANUAL: 'manual'
};

export const SKIP_CATEGORY_LABELS = {
  [SKIP_CATEGORIES.IMAGE_ONLY]: 'Image Only',
  [SKIP_CATEGORIES.LOW_QUALITY]: 'Low Quality',
  [SKIP_CATEGORIES.SPAM]: 'Spam',
  [SKIP_CATEGORIES.DUPLICATE]: 'Duplicate',
  [SKIP_CATEGORIES.LONG_TEXT]: 'Long Text',
  [SKIP_CATEGORIES.MANUAL]: 'Manual Skip'
};

export const SKIP_CATEGORY_DESCRIPTIONS = {
  [SKIP_CATEGORIES.IMAGE_ONLY]: 'Content that\'s primarily images/media',
  [SKIP_CATEGORIES.LOW_QUALITY]: 'Low-engagement or promotional content',
  [SKIP_CATEGORIES.SPAM]: 'Spam-like content',
  [SKIP_CATEGORIES.DUPLICATE]: 'Retweets or duplicated content',
  [SKIP_CATEGORIES.LONG_TEXT]: 'Very long content (>500 characters)',
  [SKIP_CATEGORIES.MANUAL]: 'Manually marked for skipping'
};

export function SkipCategoriesProvider({ children }) {
  // Map of tweet ID to skip categories array
  const [skipCategoriesMap, setSkipCategoriesMap] = useState({});

  // Local storage key for offline changes
  const STORAGE_KEY = 'skip_categories_offline';

  // Load skip categories from tweets on mount
  useEffect(() => {
    // Try to load any offline changes first
    const offlineData = localStorage.getItem(STORAGE_KEY);
    if (offlineData) {
      try {
        const parsed = JSON.parse(offlineData);
        setSkipCategoriesMap(parsed);
      } catch (error) {
        console.error('[SkipCategories] Failed to parse offline data:', error);
      }
    }
  }, []);

  // Initialize skip categories from loaded tweets
  const initializeFromTweets = useCallback((tweets) => {
    if (!Array.isArray(tweets)) return;

    setSkipCategoriesMap(prev => {
      const newMap = { ...prev };
      let hasChanges = false;

      // Load bookmark skip categories from localStorage (backup)
      const bookmarkSkips = JSON.parse(localStorage.getItem('bookmark_skip_categories') || '{}');

      tweets.forEach(tweet => {
        if (!tweet || !tweet.id) return;

        // Check for skip categories in metadata or direct field
        let categories = [];

        // Priority 1: Direct skipCategories field (includes bookmarks from overrides.json)
        if (tweet.skipCategories && Array.isArray(tweet.skipCategories)) {
          categories = tweet.skipCategories;
        }
        // Priority 2: metadata.skipCategories (API format)
        else if (tweet.metadata?.skipCategories && Array.isArray(tweet.metadata.skipCategories)) {
          categories = tweet.metadata.skipCategories;
        }
        // Priority 3: localStorage for bookmarks (fallback)
        else if (tweet.source === 'bookmark' && bookmarkSkips[tweet.id]) {
          categories = bookmarkSkips[tweet.id];
        }
        // Priority 4: Legacy skip field
        else if (tweet.skip === true || tweet.metadata?.skip === true) {
          categories = [SKIP_CATEGORIES.MANUAL];
        }

        // Only update if there are categories or if we don't have data for this tweet
        if (categories.length > 0 || !newMap[tweet.id]) {
          newMap[tweet.id] = categories;
          hasChanges = true;
        }
      });

      return hasChanges ? newMap : prev;
    });
  }, []);

  // Get skip categories for a specific tweet
  const getSkipCategories = useCallback((tweetId) => {
    return skipCategoriesMap[tweetId] || [];
  }, [skipCategoriesMap]);

  // Check if tweet has any skip categories
  const isSkipped = useCallback((tweetId) => {
    const categories = skipCategoriesMap[tweetId] || [];
    return categories.length > 0;
  }, [skipCategoriesMap]);

  // Check if tweet has specific skip category
  const hasSkipCategory = useCallback((tweetId, category) => {
    const categories = skipCategoriesMap[tweetId] || [];
    return categories.includes(category);
  }, [skipCategoriesMap]);

  // Update skip categories for a tweet
  const updateSkipCategories = useCallback(async (tweetId, categories, tweetSource = null, optimistic = true) => {
    if (!InkQuoteApi.isConfigured()) {
      console.warn('[SkipCategories] Cannot update - InkQuote API not configured');
      return { success: false, error: 'InkQuote API not configured' };
    }

    // Optimistic update
    if (optimistic) {
      setSkipCategoriesMap(prev => ({
        ...prev,
        [tweetId]: categories
      }));
    }

    // Handle bookmarks differently - save to overrides.json via dev server
    if (tweetSource === 'bookmark') {
      try {
        // Save to localStorage for immediate UI update
        const storageKey = 'bookmark_skip_categories';
        const bookmarkSkips = JSON.parse(localStorage.getItem(storageKey) || '{}');
        bookmarkSkips[tweetId] = categories;
        localStorage.setItem(storageKey, JSON.stringify(bookmarkSkips));

        // Also save to overrides.json via dev server (only in dev mode)
        if (process.env.REACT_APP_MODE === 'development') {
          const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
          const response = await fetch(`${apiUrl}/api/save-bookmark-skip-categories`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ tweetId, skipCategories: categories })
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          if (process.env.REACT_APP_MODE === 'development') {
            console.log('[SkipCategories] Saved bookmark skip categories:', tweetId, categories);
          }
        }

        return { success: true, source: 'overrides.json' };
      } catch (error) {
        console.error('[SkipCategories] Failed to save bookmark skip categories:', error);

        // Revert optimistic update on error
        if (optimistic) {
          setSkipCategoriesMap(prev => {
            const newMap = { ...prev };
            delete newMap[tweetId];
            return newMap;
          });

          // Also revert localStorage
          const storageKey = 'bookmark_skip_categories';
          const bookmarkSkips = JSON.parse(localStorage.getItem(storageKey) || '{}');
          delete bookmarkSkips[tweetId];
          localStorage.setItem(storageKey, JSON.stringify(bookmarkSkips));
        }

        return { success: false, error: error.message };
      }
    }

    // For likes and roam: Save to API using InkQuoteApi
    try {
      await InkQuoteApi.updateSkipCategories(tweetId, categories);

      // Remove from offline storage if successful
      const offlineData = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      if (offlineData[tweetId]) {
        delete offlineData[tweetId];
        localStorage.setItem(STORAGE_KEY, JSON.stringify(offlineData));
      }

      if (process.env.REACT_APP_MODE === 'development') {
        console.log('[SkipCategories] Successfully updated skip categories for:', tweetId, categories);
      }
      return { success: true };
    } catch (error) {
      console.error('[SkipCategories] Failed to update skip categories:', error);

      // Revert optimistic update on error
      if (optimistic) {
        setSkipCategoriesMap(prev => {
          const newMap = { ...prev };
          delete newMap[tweetId];
          return newMap;
        });
      }

      // Save to localStorage for offline access
      const offlineData = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      offlineData[tweetId] = categories;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(offlineData));

      return { success: false, queued: true, error: error.message };
    }
  }, []);

  // Toggle a specific skip category for a tweet
  const toggleSkipCategory = useCallback(async (tweetId, category, tweetSource = null) => {
    const currentCategories = getSkipCategories(tweetId);
    const hasCategory = currentCategories.includes(category);

    const newCategories = hasCategory
      ? currentCategories.filter(c => c !== category)
      : [...currentCategories, category];

    return updateSkipCategories(tweetId, newCategories, tweetSource);
  }, [getSkipCategories, updateSkipCategories]);

  // Clear all skip categories for a tweet
  const clearSkipCategories = useCallback(async (tweetId, tweetSource = null) => {
    return updateSkipCategories(tweetId, [], tweetSource);
  }, [updateSkipCategories]);

  // Quick action: Mark as manual skip
  const markAsManualSkip = useCallback(async (tweetId, tweetSource = null) => {
    return updateSkipCategories(tweetId, [SKIP_CATEGORIES.MANUAL], tweetSource);
  }, [updateSkipCategories]);

  // Bulk update skip categories for multiple tweets
  const bulkUpdateSkipCategories = useCallback(async (tweetIds, categories, onProgress) => {
    const results = [];
    let successCount = 0;
    let failedCount = 0;

    for (let i = 0; i < tweetIds.length; i++) {
      const tweetId = tweetIds[i];
      const result = await updateSkipCategories(tweetId, categories, true);

      results.push({ tweetId, result });

      if (result.success || result.queued) {
        successCount++;
      } else {
        failedCount++;
      }

      // Call progress callback if provided
      if (onProgress) {
        onProgress({
          current: i + 1,
          total: tweetIds.length,
          successCount,
          failedCount
        });
      }

      // Add small delay to avoid rate limiting
      if (i < tweetIds.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    return {
      success: true,
      results,
      successCount,
      failedCount
    };
  }, [updateSkipCategories]);

  // Get count of tweets with specific skip category
  const getSkipCategoryCount = useCallback((category) => {
    return Object.values(skipCategoriesMap).filter(
      categories => categories.includes(category)
    ).length;
  }, [skipCategoriesMap]);

  // Get total count of skipped tweets
  const getTotalSkippedCount = useCallback(() => {
    return Object.keys(skipCategoriesMap).filter(
      tweetId => skipCategoriesMap[tweetId].length > 0
    ).length;
  }, [skipCategoriesMap]);

  const value = {
    // State
    skipCategoriesMap,

    // Methods
    initializeFromTweets,
    getSkipCategories,
    isSkipped,
    hasSkipCategory,
    updateSkipCategories,
    toggleSkipCategory,
    clearSkipCategories,
    markAsManualSkip,
    bulkUpdateSkipCategories,

    // Statistics
    getSkipCategoryCount,
    getTotalSkippedCount,

    // Constants
    SKIP_CATEGORIES,
    SKIP_CATEGORY_LABELS,
    SKIP_CATEGORY_DESCRIPTIONS
  };

  return (
    <SkipCategoriesContext.Provider value={value}>
      {children}
    </SkipCategoriesContext.Provider>
  );
}

export function useSkipCategories() {
  const context = useContext(SkipCategoriesContext);
  if (!context) {
    throw new Error('useSkipCategories must be used within SkipCategoriesProvider');
  }
  return context;
}

export default SkipCategoriesContext;
